/**
 * A aba "Quem clicou" do redirecionador (C1–C4).
 *
 * Responde, nesta ordem: quanto clicou e quanto entrou (frase do topo), quem foi cada clique
 * (lista densa, com o detalhe da pessoa só quando se pede), de que aparelho (C2) e de onde (C4).
 *
 * Tudo o que aparece aqui foi decidido no servidor — inclusive "suspeito", "aguardando" e a
 * permanência. A tela formata e esconde/mostra; não conta.
 */
import {
  Badge,
  Button,
  ConfidenceBadge,
  EmptyState,
  ErrorState,
  Meter,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  formatNumber,
  formatPercent,
  useToast,
} from '@mgdlead/ui';
import { useMemo, useState } from 'react';
import {
  CircleHelp,
  Monitor,
  MousePointerClick,
  ShieldAlert,
  Smartphone,
  Tablet,
  type LucideIcon,
} from 'lucide-react';

import {
  ENTRY_SPEC,
  coverageSentence,
  repeatText,
  stayText,
  summarySentence,
} from '../../../features/redirector/who-clicked-ui';
import {
  useDeviceSummary,
  useRegionSummary,
  useRevealClickPhone,
  useWhoClicked,
  type Janela,
  type RegionSummary,
  type WhoClickedRow,
} from '../../../features/redirector/who-clicked-api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';

const DEVICE_ICON: Record<WhoClickedRow['device']['type'], LucideIcon> = {
  MOBILE: Smartphone,
  TABLET: Tablet,
  DESKTOP: Monitor,
  UNKNOWN: CircleHelp,
};

const PAGINA = 50;
const TETO = 200;

export interface WhoClickedPanelProps {
  workspaceId: string;
  redirectorId: string;
  timezone: string;
  /** `pii:reveal` (OWNER/ADMIN). Só esconde o botão; quem barra é o servidor. */
  canRevealPhone: boolean;
}

export function WhoClickedPanel({
  workspaceId,
  redirectorId,
  timezone,
  canRevealPhone,
}: WhoClickedPanelProps) {
  /**
   * Últimos 30 dias, rolantes — o mesmo recorte da aba Cliques. Fechado uma vez por link
   * aberto: um `new Date()` solto mudaria a chave da consulta a cada render.
   */
  const janela = useMemo((): Janela => {
    const to = new Date();
    return { from: new Date(to.getTime() - 30 * 86_400_000), to };
  }, [redirectorId]);
  const [limit, setLimit] = useState(PAGINA);

  const lista = useWhoClicked(workspaceId, redirectorId, janela, limit);
  const aparelhos = useDeviceSummary(workspaceId, redirectorId, janela);
  const regioes = useRegionSummary(workspaceId, redirectorId, janela);

  return (
    <div className="flex flex-col gap-4">
      {lista.isPending ? (
        <Carregando />
      ) : lista.isError ? (
        <ErrorState
          title="Não conseguimos carregar quem clicou"
          description={humanizeError(lista.error)}
          onRetry={() => void lista.refetch()}
        />
      ) : (
        <>
          <section className="flex flex-col gap-1">
            <p className="text-md font-medium nums">{summarySentence(lista.data.summary)}</p>
            {lista.data.summary.suspicious + lista.data.summary.duplicates > 0 ? (
              <p className="text-2xs text-muted-foreground nums">
                {formatNumber(lista.data.summary.suspicious)} suspeitos ·{' '}
                {formatNumber(lista.data.summary.duplicates)} repetidos. Todos continuam contando
                como clique; a lista só agrupa os repetidos e marca os suspeitos — quem decide tirar
                da conta é você.
              </p>
            ) : null}
            <p className="text-2xs text-muted-foreground">Últimos 30 dias.</p>
          </section>

          {lista.data.rows.length === 0 ? (
            <EmptyState
              kind="zero"
              icon={MousePointerClick}
              title="Nenhum clique neste período."
              description="Assim que alguém clicar no link, mostramos aqui se a pessoa entrou no grupo e quanto tempo ficou."
            />
          ) : (
            <>
              <ul className="flex flex-col divide-y divide-border" aria-label="Quem clicou">
                {lista.data.rows.map((row) => (
                  <li key={row.clickId}>
                    <LinhaDoClique
                      row={row}
                      timezone={timezone}
                      workspaceId={workspaceId}
                      redirectorId={redirectorId}
                      canRevealPhone={canRevealPhone}
                    />
                  </li>
                ))}
              </ul>
              {lista.data.nextBefore !== null && limit < TETO ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={lista.isFetching}
                  onClick={() => setLimit((n) => Math.min(TETO, n + PAGINA))}
                >
                  Mostrar mais cliques
                </Button>
              ) : null}
            </>
          )}
        </>
      )}

      <SecaoAparelhos dados={aparelhos.data} carregando={aparelhos.isPending} />
      <SecaoRegiao
        dados={regioes.data}
        carregando={regioes.isPending}
        erro={regioes.isError ? humanizeError(regioes.error) : null}
        onRetry={() => void regioes.refetch()}
      />
    </div>
  );
}

/* ========================================================================== */
/*  Uma linha                                                                  */
/* ========================================================================== */

function LinhaDoClique({
  row,
  timezone,
  workspaceId,
  redirectorId,
  canRevealPhone,
}: {
  row: WhoClickedRow;
  timezone: string;
  workspaceId: string;
  redirectorId: string;
  canRevealPhone: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const entrada = ENTRY_SPEC[row.entry];
  const ficou = stayText(row.stay);
  const Icone = DEVICE_ICON[row.device.type];
  const repetidos = repeatText(row.repeatCount);
  const detalheId = `quem-clicou-${row.clickId}`;

  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center gap-1">
        <Badge tone={entrada.tone} title={entrada.hint}>
          {entrada.label}
        </Badge>
        {row.entry === 'JOINED' ? (
          <ConfidenceBadge confidence={row.attribution.confidence} score={row.attribution.score} />
        ) : null}
        {row.suspicious ? (
          <Badge tone="warning" title={row.flags.map((f) => f.reason).join(' · ')}>
            <ShieldAlert aria-hidden className="size-3" />
            Suspeito
          </Badge>
        ) : null}
        <span
          className="nums ml-auto text-2xs text-muted-foreground"
          title={formatExact(row.clickedAt, timezone) ?? undefined}
        >
          {formatRelative(row.clickedAt)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1">
          <Icone aria-hidden className="size-4 text-muted-foreground" />
          {row.device.label}
        </span>
        <span title={ficou?.hint}>
          <span className="text-muted-foreground">Ficou: </span>
          {/* Quem não entrou não tem permanência: "—", não "0". */}
          {ficou?.text ?? '—'}
        </span>
        {row.groupName === null ? null : (
          <span className="text-muted-foreground">{row.groupName}</span>
        )}
      </div>

      {row.suspicious ? (
        <span className="text-2xs text-warning">{row.flags.map((f) => f.reason).join(' · ')}</span>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {repetidos === null ? null : (
          <span className="text-2xs text-muted-foreground nums">{repetidos}</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={aberto}
          aria-controls={detalheId}
          onClick={() => setAberto((v) => !v)}
        >
          {aberto ? 'Esconder detalhes' : 'Ver detalhes'}
        </Button>
      </div>

      {aberto ? (
        <DetalheDaPessoa
          id={detalheId}
          row={row}
          timezone={timezone}
          workspaceId={workspaceId}
          redirectorId={redirectorId}
          canRevealPhone={canRevealPhone}
        />
      ) : null}
    </div>
  );
}

/**
 * A linha do tempo clique → entrada → saída, a pessoa e o telefone.
 *
 * Fica dentro da linha (e não numa segunda gaveta por cima desta): no celular, gaveta sobre
 * gaveta perde o caminho de volta.
 */
function DetalheDaPessoa({
  id,
  row,
  timezone,
  workspaceId,
  redirectorId,
  canRevealPhone,
}: {
  id: string;
  row: WhoClickedRow;
  timezone: string;
  workspaceId: string;
  redirectorId: string;
  canRevealPhone: boolean;
}) {
  const { toast } = useToast();
  const revelar = useRevealClickPhone();
  const [telefone, setTelefone] = useState<string | null>(null);

  const passos: { termo: string; valor: string }[] = [
    { termo: 'Clicou', valor: formatExact(row.clickedAt, timezone) ?? '—' },
  ];
  if (row.entry === 'JOINED') {
    passos.push({ termo: 'Entrou', valor: formatExact(row.joinedAt, timezone) ?? '—' });
  } else if (row.entry === 'PENDING') {
    passos.push({
      termo: 'Pode entrar até',
      valor: formatExact(row.pendingUntil, timezone) ?? '—',
    });
  } else {
    passos.push({ termo: 'Entrou', valor: ENTRY_SPEC[row.entry].label });
  }
  if (row.stay?.state === 'LEFT') {
    passos.push({ termo: 'Saiu', valor: formatExact(row.stay.leftAt, timezone) ?? '—' });
  }

  return (
    <div id={id} className="mt-1 flex flex-col gap-2 rounded-md bg-muted p-3 text-sm">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {passos.map((p) => (
          <div key={p.termo} className="contents">
            <dt className="text-muted-foreground">{p.termo}</dt>
            <dd className="nums">{p.valor}</dd>
          </div>
        ))}
        <dt className="text-muted-foreground">Grupo</dt>
        <dd>{row.groupName ?? '—'}</dd>
        <dt className="text-muted-foreground">Pessoa</dt>
        <dd>{row.person === null ? 'Não identificada' : `Pessoa ${row.person.code}`}</dd>
        {row.person === null ? null : (
          <>
            <dt className="text-muted-foreground">Telefone</dt>
            <dd className="nums">
              {telefone ??
                row.person.maskedPhone ??
                'Não identificado (o WhatsApp só mostrou o identificador interno)'}
            </dd>
          </>
        )}
      </dl>

      {row.person?.hasPhone && canRevealPhone && telefone === null ? (
        <div>
          <Button
            variant="secondary"
            size="sm"
            loading={revelar.isPending}
            onClick={() =>
              revelar.mutate(
                { workspaceId, redirectorId, clickId: row.clickId },
                {
                  onSuccess: (r) => setTelefone(r.phone),
                  onError: (error: unknown) =>
                    toast({
                      title: 'Não deu para mostrar o telefone',
                      description: humanizeError(error),
                      tone: 'danger',
                    }),
                },
              )
            }
          >
            Mostrar telefone
          </Button>
          <p className="mt-1 text-2xs text-muted-foreground">
            Fica registrado quem viu o número, e quando.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* ========================================================================== */
/*  Aparelhos (C2) e região (C4)                                              */
/* ========================================================================== */

function SecaoAparelhos({
  dados,
  carregando,
}: {
  dados: { total: number; byOsBrowser: { label: string; clicks: number }[] } | undefined;
  carregando: boolean;
}) {
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <h3 className="text-md font-medium">Aparelhos</h3>
      {carregando ? (
        <Skeleton className="h-16 w-full" />
      ) : dados === undefined || dados.total === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum clique neste período.</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {dados.byOsBrowser.slice(0, 6).map((a) => (
            <li key={a.label} className="flex items-center justify-between gap-2">
              <span>{a.label}</span>
              <span className="nums text-muted-foreground">{formatNumber(a.clicks)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SecaoRegiao({
  dados,
  carregando,
  erro,
  onRetry,
}: {
  dados: RegionSummary | undefined;
  carregando: boolean;
  erro: string | null;
  onRetry: () => void;
}) {
  const cobertura = dados === undefined ? null : coverageSentence(dados);
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <h3 className="text-md font-medium">Por região</h3>
      {carregando ? (
        <Skeleton className="h-24 w-full" />
      ) : erro !== null ? (
        <ErrorState title="Não conseguimos montar a região" description={erro} onRetry={onRetry} />
      ) : dados === undefined || dados.people === 0 ? (
        <EmptyState
          kind="zero"
          title="Ninguém entrou nos grupos desta campanha no período."
          description="Quando houver entradas, mostramos de que estado é quem entrou, pelo DDD do telefone."
        />
      ) : (
        <>
          {cobertura === null ? null : (
            <p className="text-2xs text-muted-foreground">{cobertura}</p>
          )}
          {dados.byUf.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma das pessoas que entraram tem telefone brasileiro identificado.
            </p>
          ) : (
            <Table caption="Pessoas que entraram, por estado">
              <TableHeader>
                <TableRow>
                  <TableHead>Estado</TableHead>
                  <TableHead numeric>Pessoas</TableHead>
                  <TableHead>Participação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dados.byUf.map((u) => (
                  <TableRow key={u.uf}>
                    <TableRowHeader>
                      {u.uf}
                      <span className="ml-1 text-2xs text-muted-foreground">
                        DDD{' '}
                        {dados.byDdd
                          .filter((d) => d.uf === u.uf)
                          .map((d) => d.ddd)
                          .join(', ')}
                      </span>
                    </TableRowHeader>
                    <TableCell numeric>{formatNumber(u.people)}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <Meter
                          value={u.share}
                          segments={10}
                          size="sm"
                          label={`${u.uf}: ${formatPercent(u.share, 0)}`}
                        />
                        <span className="nums text-2xs text-muted-foreground">
                          {formatPercent(u.share, 0)}
                        </span>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="text-2xs text-muted-foreground nums">
            Sem DDD (o WhatsApp não mostrou o número): {formatNumber(dados.noPhone)}
            {dados.foreign > 0 ? ` · De fora do Brasil: ${formatNumber(dados.foreign)}` : ''}
            {dados.invalid > 0 ? ` · Número que não confere: ${formatNumber(dados.invalid)}` : ''}
          </p>
        </>
      )}
    </section>
  );
}

function Carregando() {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
      <span className="sr-only">Carregando</span>
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}
